import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import * as zod from 'zod';
import {
  REPORT_SUBSCRIPTION_FREQUENCIES,
  REPORT_SUBSCRIPTION_TYPES,
  ReportSubscription,
  ReportSubscriptionFrequency,
  ReportSubscriptionType,
} from '@/@types/report-subscription';
import { User } from '@/@types/user';
import {
  REPORT_SUBSCRIPTION_FREQUENCY_LABELS,
  REPORT_SUBSCRIPTION_TYPE_LABELS,
} from '@/modules/report-subscriptions/constants';

const reportSubscriptionFormSchema = zod.object({
  id: zod.string().optional(),
  userId: zod.string().min(1, 'Informe o usuário'),
  reportType: zod.enum([
    'daily-sales-summary',
    'weekly-sales-summary',
    'monthly-sales-summary',
  ]),
  frequency: zod.enum(['daily', 'weekly', 'monthly']),
  isActive: zod.boolean(),
});

export type ReportSubscriptionFormData = zod.infer<
  typeof reportSubscriptionFormSchema
>;

interface ReportSubscriptionFormProps {
  reportSubscription?: ReportSubscription;
  users: User[];
  isLoading: boolean;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (data: ReportSubscriptionFormData) => void;
  onCancel: () => void;
}

export function ReportSubscriptionForm({
  reportSubscription,
  users,
  isLoading,
  onSubmit,
  onCancel,
}: ReportSubscriptionFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ReportSubscriptionFormData>({
    resolver: zodResolver(reportSubscriptionFormSchema),
    defaultValues: {
      id: reportSubscription?.id || '',
      userId: reportSubscription?.userId || '',
      reportType: reportSubscription?.reportType || 'daily-sales-summary',
      frequency: reportSubscription?.frequency || 'daily',
      isActive: reportSubscription?.isActive ?? true,
    },
  });

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <DialogContent dividers sx={{ py: 3, px: 2 }}>
        <Stack spacing={3}>
          <FormControl fullWidth error={!!errors.userId}>
            <Controller
              name="userId"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => option.name}
                  noOptionsText="Nenhum registro encontrado"
                  autoHighlight
                  value={
                    value ? users?.find((user) => user.id === value) : null
                  }
                  onChange={(event, newValue) =>
                    onChange(newValue ? newValue.id : '')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...field}
                      {...params}
                      label="Usuário"
                      error={!!errors.userId}
                    />
                  )}
                />
              )}
            />
            {!!errors.userId && (
              <FormHelperText>{errors.userId.message}</FormHelperText>
            )}
          </FormControl>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <FormControl fullWidth error={!!errors.reportType}>
              <Controller
                name="reportType"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <Autocomplete
                    options={REPORT_SUBSCRIPTION_TYPES}
                    getOptionLabel={(option) =>
                      REPORT_SUBSCRIPTION_TYPE_LABELS[
                        option as ReportSubscriptionType
                      ]
                    }
                    noOptionsText="Nenhum registro encontrado"
                    autoHighlight
                    value={value || null}
                    onChange={(event, newValue) => onChange(newValue || '')}
                    renderInput={(params) => (
                      <TextField
                        {...field}
                        {...params}
                        label="Relatório"
                        error={!!errors.reportType}
                      />
                    )}
                  />
                )}
              />
              {!!errors.reportType && (
                <FormHelperText>{errors.reportType.message}</FormHelperText>
              )}
            </FormControl>
            <FormControl fullWidth error={!!errors.frequency}>
              <Controller
                name="frequency"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <Autocomplete
                    options={REPORT_SUBSCRIPTION_FREQUENCIES}
                    getOptionLabel={(option) =>
                      REPORT_SUBSCRIPTION_FREQUENCY_LABELS[
                        option as ReportSubscriptionFrequency
                      ]
                    }
                    noOptionsText="Nenhum registro encontrado"
                    autoHighlight
                    value={value || null}
                    onChange={(event, newValue) => onChange(newValue || '')}
                    renderInput={(params) => (
                      <TextField
                        {...field}
                        {...params}
                        label="Frequência"
                        error={!!errors.frequency}
                      />
                    )}
                  />
                )}
              />
              {!!errors.frequency && (
                <FormHelperText>{errors.frequency.message}</FormHelperText>
              )}
            </FormControl>
          </Stack>
          <Controller
            name="isActive"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value}
                    onChange={(event) => onChange(event.target.checked)}
                  />
                }
                label="Ativo?"
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          Salvar
        </Button>
      </DialogActions>
    </Box>
  );
}
